/* eslint-disable new-cap, @typescript-eslint/no-empty-function */
/**
 * Company Groups API hooks (ISSUE-803 Phase 1C).
 */
import { restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

export {
  COMPANY_GROUP_COLORS,
  type CompanyGroupColor,
} from "utils/company-group-colors";

// =====================================
// Types
// =====================================

export type CompanyGroup = {
  id: string;
  name: string;
  color: string | null;
  description: string | null;
  companyCount: number;
  createdAt: string;
  updatedAt: string;
};

export type CompanyGroupDetail = {
  id: string;
  name: string;
  color: string | null;
  description: string | null;
  companyIds: string[];
  createdAt: string;
  updatedAt: string;
};

export type CreateCompanyGroupInput = {
  name: string;
  color?: string | null;
  description?: string | null;
};

export type UpdateCompanyGroupInput = {
  name?: string;
  color?: string | null;
  description?: string | null;
};

export type GroupListResponse = {
  items: CompanyGroup[];
};

export type GroupMemberCompany = {
  id: string;
  name: string;
  normalizedName: string;
  primaryDomain: string | null;
  contactCount: number;
};

export type RecipientPreviewItem = {
  id: string;
  email: string;
  displayName: string | null;
  companyId: string;
  companyName: string;
};

export type RecipientsPreviewResponse = {
  companyCount: number;
  activeContactCount: number;
  optedOutCount: number;
  contacts: RecipientPreviewItem[];
};

// =====================================
// List groups
// =====================================

export type UseCompanyGroupsApiResult = {
  data: GroupListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<GroupListResponse>;
};

/**
 * 企業グループ一覧を取得する hook。
 * @param {object} [options] スキップフラグ
 * @param {boolean} [options.skip] true でフェッチを抑制
 * @return {UseCompanyGroupsApiResult} data, error, loading, refetch
 */
export function useCompanyGroupsApi(
  options: { skip?: boolean } = {},
): UseCompanyGroupsApiResult {
  const [data, setData] = useState<GroupListResponse | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchList = useCallback(async (): Promise<GroupListResponse> => {
    setLoading(true);
    setError(undefined);
    try {
      const res = await restClient.get<GroupListResponse>("/company-groups");
      setData(res);
      return res;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!options.skip) {
      fetchList().catch(() => undefined);
    }
  }, [options.skip, fetchList]);

  return { data, error, loading, refetch: fetchList };
}

// =====================================
// Group detail
// =====================================

export type UseCompanyGroupApiOptions = {
  id: string;
  skip?: boolean;
};

export type UseCompanyGroupApiResult = {
  data: CompanyGroupDetail | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<CompanyGroupDetail>;
};

/**
 * 企業グループ詳細を取得する hook。
 * @param {UseCompanyGroupApiOptions} options ID とスキップフラグ
 * @return {UseCompanyGroupApiResult} data, error, loading, refetch
 */
export function useCompanyGroupApi(
  options: UseCompanyGroupApiOptions,
): UseCompanyGroupApiResult {
  const [data, setData] = useState<CompanyGroupDetail | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchOne = useCallback(async (): Promise<CompanyGroupDetail> => {
    setLoading(true);
    setError(undefined);
    try {
      const res = await restClient.get<CompanyGroupDetail>(
        `/company-groups/${options.id}`,
      );
      setData(res);
      return res;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [options.id]);

  useEffect(() => {
    if (!options.skip && options.id) {
      fetchOne().catch(() => undefined);
    }
  }, [options.skip, options.id, fetchOne]);

  return { data, error, loading, refetch: fetchOne };
}

// =====================================
// Group member companies
// =====================================

/**
 * 企業グループ所属の企業一覧を取得する hook。
 * @param {object} options 企業グループ ID とスキップフラグ
 * @param {string} options.groupId 企業グループ ID
 * @param {boolean} [options.skip] true でフェッチを抑制
 * @return {object} data, error, loading, refetch
 */
export function useGroupMemberCompaniesApi(options: {
  groupId: string;
  skip?: boolean;
}): {
  data: { items: GroupMemberCompany[] } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<{ items: GroupMemberCompany[] }>;
} {
  const [data, setData] = useState<{ items: GroupMemberCompany[] } | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(!options.skip);

  const fetchList = useCallback(async (): Promise<{
    items: GroupMemberCompany[];
  }> => {
    setLoading(true);
    setError(undefined);
    try {
      const res = await restClient.get<{ items: GroupMemberCompany[] }>(
        `/company-groups/${options.groupId}/companies`,
      );
      setData(res);
      return res;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [options.groupId]);

  useEffect(() => {
    if (!options.skip && options.groupId) {
      fetchList().catch(() => undefined);
    }
  }, [options.skip, options.groupId, fetchList]);

  return { data, error, loading, refetch: fetchList };
}

// =====================================
// Mutations
// =====================================

export type UseCompanyGroupMutationsResult = {
  createGroup: (input: CreateCompanyGroupInput) => Promise<CompanyGroup>;
  updateGroup: (
    id: string,
    input: UpdateCompanyGroupInput,
  ) => Promise<CompanyGroup>;
  deleteGroup: (id: string) => Promise<void>;
  addCompany: (
    groupId: string,
    companyId: string,
  ) => Promise<{ id: string; groupId: string; companyId: string }>;
  removeCompany: (groupId: string, companyId: string) => Promise<void>;
  recipientsPreview: (groupId: string) => Promise<RecipientsPreviewResponse>;
  loading: boolean;
  error: Error | undefined;
};

/**
 * 企業グループ書き込み mutation hooks をまとめて返す。
 * @return {UseCompanyGroupMutationsResult} mutations と loading, error
 */
export function useCompanyGroupMutations(): UseCompanyGroupMutationsResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const wrap = useCallback(async <T>(fn: () => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(undefined);
    try {
      return await fn();
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createGroup = useCallback(
    (input: CreateCompanyGroupInput) =>
      wrap(() => restClient.post<CompanyGroup>("/company-groups", input)),
    [wrap],
  );

  const updateGroup = useCallback(
    (id: string, input: UpdateCompanyGroupInput) =>
      wrap(() =>
        restClient.patch<CompanyGroup>(`/company-groups/${id}`, input),
      ),
    [wrap],
  );

  const deleteGroup = useCallback(
    (id: string) =>
      wrap(() => restClient.delete<void>(`/company-groups/${id}`)),
    [wrap],
  );

  const addCompany = useCallback(
    (groupId: string, companyId: string) =>
      wrap(() =>
        restClient.post<{ id: string; groupId: string; companyId: string }>(
          `/company-groups/${groupId}/companies`,
          { companyId },
        ),
      ),
    [wrap],
  );

  const removeCompany = useCallback(
    (groupId: string, companyId: string) =>
      wrap(() =>
        restClient.delete<void>(
          `/company-groups/${groupId}/companies/${companyId}`,
        ),
      ),
    [wrap],
  );

  const recipientsPreview = useCallback(
    (groupId: string) =>
      wrap(() =>
        restClient.get<RecipientsPreviewResponse>(
          `/company-groups/${groupId}/recipients-preview`,
        ),
      ),
    [wrap],
  );

  return {
    createGroup,
    updateGroup,
    deleteGroup,
    addCompany,
    removeCompany,
    recipientsPreview,
    loading,
    error,
  };
}
